import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ClipboardCopy, Wand2, FileAudio, Check, ChevronRight } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

// WhatsApp SVG icon
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

function StepBadge({ step, label, status }) {
  const styles = {
    done: "bg-emerald-100 text-emerald-700 border-emerald-200",
    active: "bg-[#E8542A]/10 text-[#E8542A] border-[#E8542A]/30",
    pending: "bg-[#F3EFEA] text-[#8A6D5C] border-[#E8D5C8]",
  };
  return (
    <div className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${styles[status]}`}>
      {status === "done" ? <Check className="h-3 w-3" /> : <span>{step}</span>}
      {label}
    </div>
  );
}

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);
  const [justGenerated, setJustGenerated] = useState(false);
  const whatsappRef = useRef(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        return api.get(`/sessions/${id}`);
      })
      .then((r) => {
        setSession(r.data);
        setMessage(r.data.generated_message || "");
      })
      .catch(() => navigate("/login"));
  }, [id, navigate]);

  if (!user || !session) return null;

  const hasAudio =
    (session.audio_file_ids && session.audio_file_ids.length > 0) ||
    !!session.audio_file_id;
  const segmentCount = session.audio_file_ids?.length || (session.audio_file_id ? 1 : 0);
  const primaryAudioId =
    (session.audio_file_ids && session.audio_file_ids[0]) || session.audio_file_id;

  const transcribe = async () => {
    setBusy("transcribe");
    try {
      const { data } = await api.post(`/sessions/${id}/transcribe`);
      setSession((s) => ({ ...s, transcript: data.transcript }));
      toast.success(
        data.segments > 1
          ? `Transcript ready (${data.segments} parts merged)`
          : "Transcript ready"
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const generate = async () => {
    setBusy("generate");
    try {
      const { data } = await api.post(`/sessions/${id}/generate`);
      setMessage(data.generated_message);
      setSession((s) => ({ ...s, generated_message: data.generated_message }));
      setJustGenerated(true);
      toast.success("Parent update ready — tap Send on WhatsApp");
      // Auto-scroll to WhatsApp button after short delay
      setTimeout(() => {
        whatsappRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const saveMessage = async () => {
    await api.patch(`/sessions/${id}`, { generated_message: message }).catch((e) =>
      toast.error(apiError(e))
    );
    toast.success("Saved");
  };

  const copy = async () => {
    await navigator.clipboard.writeText(message);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

  // Step status
  const step1 = hasAudio ? "done" : "active";
  const step2 = session.transcript ? "done" : hasAudio ? "active" : "pending";
  const step3 = message ? "done" : session.transcript ? "active" : "pending";

  const transcriptPlaceholder = hasAudio
    ? "Click 'Transcribe audio' to convert your recording to text."
    : "No recording attached to this session.";

  return (
    <main data-testid="session-detail-page" className="min-h-screen bg-[#FDF3EC]">
      <TopBar user={user} />
      <div className="mx-auto w-full max-w-3xl px-5 py-10 md:px-8">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <h1 data-testid="session-title" className="font-display text-3xl font-bold text-[#1A1918] md:text-4xl">
            {session.subject || "Class"}
            {session.standard ? ` — Std ${session.standard}` : ""}
            {session.division ? ` ${session.division}` : ""}
          </h1>
          <p data-testid="session-meta" className="mt-1 text-sm text-[#8A6D5C]">
            {session.school_name} · {session.teacher_name} · {new Date(session.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </p>

          {/* Progress strip */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <StepBadge step="1" label="Recorded" status={step1} />
            <ChevronRight className="h-3.5 w-3.5 text-[#D0C4BB]" />
            <StepBadge step="2" label="Transcribed" status={step2} />
            <ChevronRight className="h-3.5 w-3.5 text-[#D0C4BB]" />
            <StepBadge step="3" label="Update ready" status={step3} />
          </div>
        </motion.div>

        <div className="mt-8 space-y-5">

          {/* Audio */}
          {hasAudio && (
            <motion.section
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="rounded-2xl border border-black/5 bg-white p-6"
            >
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 text-sm font-bold text-[#1A1918]">
                  <FileAudio className="h-4 w-4 text-[#E8542A]" /> Recording
                  {segmentCount > 1 && (
                    <span className="rounded-full bg-[#E8542A]/10 px-2.5 py-0.5 text-xs font-semibold text-[#E8542A]">
                      {segmentCount} parts
                    </span>
                  )}
                </p>
              </div>
              <audio
                data-testid="session-audio"
                controls
                className="mt-3 w-full"
                src={fileUrl(primaryAudioId)}
              />
              {segmentCount > 1 && (
                <p className="mt-2 text-xs text-[#8A6D5C]">
                  Long class split into {segmentCount} parts — all parts will be transcribed together.
                </p>
              )}
            </motion.section>
          )}

          {/* Photos */}
          {session.photo_ids.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              className="rounded-2xl border border-black/5 bg-white p-6"
            >
              <p className="text-sm font-bold text-[#1A1918]">Class photos</p>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {session.photo_ids.map((pid) => (
                  <img
                    key={pid}
                    data-testid={`session-photo-${pid}`}
                    src={fileUrl(pid)}
                    alt="Class"
                    className="h-32 w-full rounded-xl object-cover"
                  />
                ))}
              </div>
            </motion.section>
          )}

          {/* Transcript */}
          <motion.section
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="rounded-2xl border border-black/5 bg-white p-6"
          >
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-bold text-[#1A1918]">Transcript</p>
              {hasAudio && !session.transcript && (
                <Button
                  data-testid="transcribe-btn"
                  onClick={transcribe}
                  disabled={busy === "transcribe"}
                  className="rounded-xl bg-[#E8542A] text-sm text-white hover:bg-[#C94520]"
                >
                  {busy === "transcribe" ? "Transcribing..." : "Transcribe audio"}
                </Button>
              )}
            </div>
            <p
              data-testid="transcript-text"
              className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-[#5C5A56]"
            >
              {session.transcript || transcriptPlaceholder}
            </p>
            {session.transcript && (
              <Button
                data-testid="generate-btn"
                onClick={generate}
                disabled={busy === "generate"}
                className="mt-5 w-full rounded-xl bg-[#E8542A] py-5 text-sm font-bold text-white hover:bg-[#C94520]"
              >
                <Wand2 className="mr-2 h-4 w-4" />
                {busy === "generate" ? "Generating update..." : "Generate parent update"}
              </Button>
            )}
          </motion.section>

          {/* Generated message + WhatsApp */}
          {message && (
            <motion.section
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl border border-black/5 bg-white p-6"
            >
              <p className="text-sm font-bold text-[#1A1918]">WhatsApp-ready update</p>
              <p className="mt-0.5 text-xs text-[#8A6D5C]">Edit if needed, then send directly to parents</p>

              <Textarea
                data-testid="generated-message-input"
                value={message}
                onChange={(e) => { setMessage(e.target.value); setJustGenerated(false); }}
                rows={10}
                className="mt-3 rounded-xl border-[#E8D5C8] text-sm leading-relaxed focus:border-[#E8542A]"
              />

              {/* Secondary actions */}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  data-testid="save-message-btn"
                  onClick={saveMessage}
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-[#E8D5C8] text-[#8A6D5C] hover:border-[#E8542A]/40 hover:text-[#E8542A]"
                >
                  Save edits
                </Button>
                <Button
                  data-testid="copy-message-btn"
                  onClick={copy}
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-[#E8D5C8] text-[#8A6D5C] hover:border-[#E8542A]/40 hover:text-[#E8542A]"
                >
                  {copied ? (
                    <><Check className="mr-1.5 h-3.5 w-3.5 text-emerald-600" /><span className="text-emerald-600">Copied!</span></>
                  ) : (
                    <><ClipboardCopy className="mr-1.5 h-3.5 w-3.5" /> Copy text</>
                  )}
                </Button>
              </div>

              {/* PRIMARY WhatsApp CTA — animates in fresh on every generation */}
              <div ref={whatsappRef} className="mt-5">
                <AnimatePresence mode="wait">
                  <motion.a
                    key={justGenerated ? "fresh" : "existing"}
                    data-testid="whatsapp-share-link"
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={justGenerated ? { opacity: 0, y: 20, scale: 0.95 } : { opacity: 1, y: 0, scale: 1 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 24 }}
                    className={`group flex w-full items-center justify-center gap-3 rounded-2xl py-4 text-base font-bold text-white shadow-lg transition-all duration-200 hover:gap-4 active:scale-[0.98] ${
                      justGenerated
                        ? "bg-[#25D366] shadow-[#25D366]/40 hover:bg-[#1DAD56] hover:shadow-[#25D366]/50"
                        : "bg-[#25D366] shadow-[#25D366]/25 hover:bg-[#1DAD56]"
                    }`}
                  >
                    <WhatsAppIcon className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                    Send to parents on WhatsApp
                  </motion.a>
                </AnimatePresence>

                <motion.p
                  initial={justGenerated ? { opacity: 0 } : { opacity: 1 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: justGenerated ? 0.4 : 0 }}
                  className="mt-2.5 text-center text-xs text-[#8A6D5C]"
                >
                  Opens WhatsApp with your message ready to send — no copy-paste needed
                </motion.p>
              </div>
            </motion.section>
          )}
        </div>
      </div>
    </main>
  );
}
